/**
 * Web Components Initialization for TC Issue Management UI
 *
 * This file registers all required web components from @tcweb/connect-ui
 * that are used throughout the tc-issue-management-ui library.
 *
 * Note: modus-wc-icon is not registered here as it is handled globally by
 * the Modus Design System initialization in the main application.
 *
 * Import this file early in your application to ensure all web components
 * are available before any UI components that use them are rendered.
 */

import {
  defineSolidRightpanel,
  defineSolidIconButton,
  defineSolidDropDownMenu,
  defineSolidCustomInput,
  defineSolidCustomDatePicker,
  defineSolidCustomTextarea,
  defineSolidTagEditor,
  defineSolidCustomSelect,
  defineSolidEmpty,
  defineSolidPanelLayout,
  defineSolidPopup,
  defineSolidLimitVisibleItems,
  defineSolidFabButton,
  defineSolidAuthImage,
  defineSolidRestrictedAccessSplashScreen,
  defineSolidCustomTooltip,
} from '@tcweb/connect-ui';

let isInitialized = false;

/**
 * Initialize all required web components for tc-issue-management-ui
 * This function is safe to call multiple times (idempotent)
 */
export function initWebComponents(): void {
  if (isInitialized) {
    console.log('Web components already initialized, skipping...');
    return;
  }

  try {
    // Register all required web components
    defineSolidRightpanel();
    defineSolidIconButton();
    defineSolidDropDownMenu();
    defineSolidCustomInput();
    defineSolidCustomDatePicker();
    defineSolidCustomTextarea();
    defineSolidTagEditor();
    defineSolidEmpty();
    defineSolidPopup();
    defineSolidCustomSelect();
    defineSolidPanelLayout();
    defineSolidLimitVisibleItems();
    defineSolidFabButton();
    defineSolidAuthImage();
    defineSolidRestrictedAccessSplashScreen();
    defineSolidCustomTooltip();

    isInitialized = true;

    // // console.log(
    // //   '✅ TC Issue Management web components initialized successfully:'
    // // );
    // // console.log('  - tc-rightpanel');
    // // console.log('  - tc-icon-button');
    // // console.log('  - tc-dropdown-menu');
    // // console.log('  - tc-custom-input');
    // // console.log('  - tc-custom-datepicker');
    // // console.log('  - tc-custom-textarea');
    // // console.log('  - tc-tag-editor');
    // // console.log('  - tc-empty');
    // // console.log('  - tc-popup');
    // // console.log('  - tc-custom-select');
    // // console.log('  - tc-panel-layout');
    // // console.log('  - tc-limit-visible-items');
    // // console.log('  - tc-fab-button');
    // // console.log('  - tc-auth-image');
    // // console.log('  - tc-restricted-access-splash-screen');
    // // console.log('  - tc-custom-tooltip');
    //
    // // Debug: Verify registration
    // const components = [
    //   'tc-rightpanel',
    //   'tc-icon-button',
    //   'tc-dropdown-menu',
    //   'tc-custom-input',
    //   'tc-custom-datepicker',
    //   'tc-custom-textarea',
    //   'tc-tag-editor',
    //   'tc-empty',
    //   'tc-popup',
    //   'tc-custom-select',
    //   'tc-panel-layout',
    //   'tc-limit-visible-items',
    //   'tc-fab-button',
    //   'tc-auth-image',
    //   'tc-restricted-access-splash-screen',
    //   'tc-custom-tooltip',
    // ];
    //
    // components.forEach((name) => {
    //   const isRegistered = customElements.get(name) !== undefined;
    //   console.log(`  ${name}: ${isRegistered ? '✅' : '❌'}`);
    // });
  } catch (error) {
    console.error(
      '❌ Failed to initialize TC Issue Management web components:',
      error
    );
    throw error;
  }
}

/**
 * Check if web components have been initialized
 */
export function areWebComponentsInitialized(): boolean {
  return isInitialized;
}

/**
 * Get the registration status of all required web components
 */
export function getWebComponentStatus(): Record<string, boolean> {
  return {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-rightpanel is a custom web component name
    'tc-rightpanel': customElements.get('tc-rightpanel') !== undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-icon-button is a custom web component name
    'tc-icon-button': customElements.get('tc-icon-button') !== undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-dropdown-menu is a custom web component name
    'tc-dropdown-menu': customElements.get('tc-dropdown-menu') !== undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-custom-input is a custom web component name
    'tc-custom-input': customElements.get('tc-custom-input') !== undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-custom-datepicker is a custom web component name
    'tc-custom-datepicker':
      customElements.get('tc-custom-datepicker') !== undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-custom-textarea is a custom web component name
    'tc-custom-textarea':
      customElements.get('tc-custom-textarea') !== undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-tag-editor is a custom web component name
    'tc-tag-editor': customElements.get('tc-tag-editor') !== undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-empty is a custom web component name
    'tc-empty': customElements.get('tc-empty') !== undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-popup is a custom web component name
    'tc-popup': customElements.get('tc-popup') !== undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-custom-select is a custom web component name
    'tc-custom-select': customElements.get('tc-custom-select') !== undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-panel-layout is a custom web component name
    'tc-panel-layout': customElements.get('tc-panel-layout') !== undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-limit-visible-items is a custom web component name
    'tc-limit-visible-items':
      customElements.get('tc-limit-visible-items') !== undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-fab-button is a custom web component name
    'tc-fab-button': customElements.get('tc-fab-button') !== undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-auth-image is a custom web component name
    'tc-auth-image': customElements.get('tc-auth-image') !== undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-restricted-access-splash-screen is a custom web component name
    'tc-restricted-access-splash-screen':
      customElements.get('tc-restricted-access-splash-screen') !== undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- tc-custom-tooltip is a custom web component name
    'tc-custom-tooltip': customElements.get('tc-custom-tooltip') !== undefined,
  };
}
